import * as React from 'react';
import _ from 'lodash';

import styles from './index.module.css';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import * as api from '../../../api';
import * as logger from '../../../logger';

import { GroupInputs } from '../../GroupInputs';
import LabeledInput from '../../LabeledInput';
import LivesWithModalContent from './ModalContent/LivesWith';
import { ModalInput } from '../../ModalInput';
import RisksModalContent from './ModalContent/Risks';
import { SelectInput } from '../../SelectInput';
import { TextInput } from '../TextInput';
import { getOptionsIntersection } from '../../utils';
import { withTranslation, WithTranslation } from 'react-i18next';

interface EnvironmentOptions {
  livesWithPreferenceOptions: api.PatientEnvironment.LivesWith[];
  livingSituationPreferenceOptions: api.PatientEnvironment.LivingSituation[];
  parentsMaritalStatusPreferenceOptions: api.PatientEnvironment.ParentsMaritalStatus[];
  risksPreferenceOptions: api.PatientEnvironment.Risk[];
}

interface Props extends WithTranslation {
  saveFieldValue: (value: string) => void;
  updateFieldValue: (value: string) => void;
  value: string;
  fieldOptions: EnvironmentOptions;
  isPediatric?: boolean;
}

interface State {
  patientEnvironment: Partial<api.PatientEnvironment.PatientEnvironment> | null;
  isLivesWithModalOpen: boolean;
  isRisksModalOpen: boolean;
}

class Environment extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { value } = props;

    this.state = {
      patientEnvironment: null,
      isLivesWithModalOpen: false,
      isRisksModalOpen: false,
    };

    if (props.value) {
      try {
        const patientEnvironment = JSON.parse(value);
        this.state = {
          patientEnvironment,
          isLivesWithModalOpen: false,
          isRisksModalOpen: false,
        };
      } catch (e) {
        logger.error(
          e,
          `Environment: Failed parsing form field value ${value}`,
        );
      }
    }
  }

  setIsLivesWithModalOpen = (newState: boolean) => {
    this.setState({ isLivesWithModalOpen: newState });
  };

  setIsRisksModalOpen = (newState: boolean) => {
    this.setState({ isRisksModalOpen: newState });
  };

  updateFormField = (
    updatedPatientEnvironment: Partial<api.PatientEnvironment.PatientEnvironment>,
  ) => {
    const { updateFieldValue, saveFieldValue } = this.props;

    updateFieldValue(JSON.stringify(updatedPatientEnvironment));
    saveFieldValue(JSON.stringify(updatedPatientEnvironment));
  };

  updatePatientEnvironment = (
    field: string,
    newValue:
      | null
      | string
      | boolean
      | api.PatientEnvironment.LivesWith[]
      | api.PatientEnvironment.ParentsMaritalStatus
      | api.PatientEnvironment.Risk[],
  ) => {
    const { patientEnvironment } = this.state;

    const newPatientEnvironment = {
      ...patientEnvironment,
      [field]: newValue,
    };

    this.setState({
      patientEnvironment: newPatientEnvironment,
    });
    this.updateFormField(newPatientEnvironment);
  };

  getOptions = () => {
    const { fieldOptions, t } = this.props;
    const {
      livesWithPreferenceOptions,
      livingSituationPreferenceOptions,
      parentsMaritalStatusPreferenceOptions,
      risksPreferenceOptions,
    } = fieldOptions;

    const familyFriendsAvailableToHelpOptions = [
      {
        display: t('Yes'),
        key: 'true',
        value: true,
      },
      {
        display: t('No'),
        key: 'false',
        value: false,
      },
    ];
    const livesWithOptions = getOptionsIntersection(
      api.PatientEnvironment.LivesWith,
      livesWithPreferenceOptions,
    ).map((livesWith) => {
      return {
        display: t(api.PatientEnvironment.livesWithMap[livesWith]),
        key: livesWith,
        value: livesWith,
      };
    });
    const livingSituationOptions = getOptionsIntersection(
      api.PatientEnvironment.LivingSituation,
      livingSituationPreferenceOptions,
    ).map((situation) => {
      return {
        display: t(api.PatientEnvironment.livingSituationMap[situation]),
        key: situation,
        value: situation,
      };
    });
    const parentsMaritalStatusOptions = getOptionsIntersection(
      api.PatientEnvironment.ParentsMaritalStatus,
      parentsMaritalStatusPreferenceOptions,
    ).map((maritalStatus) => {
      return {
        display: t(
          api.PatientEnvironment.parentsMaritalStatusMap[maritalStatus],
        ),
        key: maritalStatus,
        value: maritalStatus,
      };
    });
    const risksOptions = getOptionsIntersection(
      api.PatientEnvironment.Risk,
      risksPreferenceOptions,
    ).map((risk) => {
      return {
        display: t(api.PatientEnvironment.risksMap[risk]),
        key: risk,
        value: risk,
      };
    });

    return {
      familyFriendsAvailableToHelpOptions,
      livesWithOptions,
      livingSituationOptions,
      risksOptions,
      parentsMaritalStatusOptions,
    };
  };

  render() {
    const { isPediatric, t } = this.props;
    const { patientEnvironment, isLivesWithModalOpen, isRisksModalOpen } =
      this.state;
    const {
      livesWith,
      livingSituation,
      parentsMaritalStatus,
      risks,
      familyFriendsAvailableToHelp,
      parentsGuardianInfo,
      siblingInfo,
    } = patientEnvironment || {};

    const uniqueLivesWith = _.uniq(livesWith);
    const uniqueRisks = _.uniq(risks);

    const {
      familyFriendsAvailableToHelpOptions,
      livesWithOptions,
      livingSituationOptions,
      risksOptions,
      parentsMaritalStatusOptions,
    } = this.getOptions();

    return (
      <div className={styles.Environment}>
        <GroupInputs>
          <LabeledInput
            label={t('Who do you live with?')}
            htmlFor="environment-live-with"
          >
            <ModalInput
              id="environment-live-with"
              children={
                <LivesWithModalContent
                  livesWith={uniqueLivesWith}
                  livesWithOptions={livesWithOptions}
                  updateLivesWith={(
                    newValue: api.PatientEnvironment.LivesWith[],
                  ) => {
                    this.updatePatientEnvironment('livesWith', newValue);
                  }}
                  setIsLivesWithModalOpen={this.setIsLivesWithModalOpen}
                />
              }
              isOpen={isLivesWithModalOpen}
              setIsOpen={this.setIsLivesWithModalOpen}
              placeholder={t('Select an option')}
              endIcon={
                <ArrowDropDownIcon className={styles.ArrowDropDownIcon} />
              }
              onModalClose={() => this.setIsLivesWithModalOpen(false)}
              initialValue={uniqueLivesWith
                .map((livesWith) =>
                  t(api.PatientEnvironment.livesWithMap[livesWith]),
                )
                .sort()
                .join(', ')}
            />
          </LabeledInput>
          <LabeledInput
            label={t('Living situation:')}
            htmlFor="environment-living-situation"
          >
            <SelectInput
              id="environment-living-situation"
              value={livingSituation}
              onChange={(newValue) =>
                this.updatePatientEnvironment('livingSituation', newValue)
              }
              options={livingSituationOptions}
            />
          </LabeledInput>
          {isPediatric ? (
            <LabeledInput
              label={t('Parental marital status:')}
              htmlFor="environment-ped-parent-marital"
            >
              <SelectInput
                id="environment-ped-parent-marital"
                value={parentsMaritalStatus}
                onChange={(newValue) =>
                  this.updatePatientEnvironment(
                    'parentsMaritalStatus',
                    newValue,
                  )
                }
                options={parentsMaritalStatusOptions}
              />
            </LabeledInput>
          ) : null}
          <LabeledInput
            label={t('Environmental risks:')}
            htmlFor="environment-risks"
          >
            <ModalInput
              id="environment-risks"
              children={
                <RisksModalContent
                  risks={uniqueRisks}
                  risksOptions={risksOptions}
                  updateRisks={(newValue) => {
                    this.updatePatientEnvironment('risks', newValue);
                  }}
                  setIsRisksModalOpen={this.setIsRisksModalOpen}
                />
              }
              isOpen={isRisksModalOpen}
              setIsOpen={this.setIsRisksModalOpen}
              placeholder={t('Select an option')}
              endIcon={
                <ArrowDropDownIcon className={styles.ArrowDropDownIcon} />
              }
              onModalClose={() => this.setIsRisksModalOpen(false)}
              initialValue={uniqueRisks
                .map((risk) => t(api.PatientEnvironment.risksMap[risk]))
                .sort()
                .join(', ')}
            />
          </LabeledInput>
          <LabeledInput
            label={t('Friends or family available to help?')}
            htmlFor="environment-family-help"
          >
            <SelectInput
              id="environment-family-help"
              value={familyFriendsAvailableToHelp}
              onChange={(newValue) =>
                this.updatePatientEnvironment(
                  'familyFriendsAvailableToHelp',
                  newValue,
                )
              }
              options={familyFriendsAvailableToHelpOptions}
            />
          </LabeledInput>
          {isPediatric ? (
            <>
              <LabeledInput
                label={t('Names of parent(s) / guardian(s):')}
                htmlFor="environment-ped-parent-name"
              >
                <TextInput
                  id="environment-ped-parent-name"
                  value={parentsGuardianInfo || ''}
                  updateFieldValue={(newValue) =>
                    this.updatePatientEnvironment(
                      'parentsGuardianInfo',
                      newValue,
                    )
                  }
                  saveFieldValue={(newValue) =>
                    this.updatePatientEnvironment(
                      'parentsGuardianInfo',
                      newValue,
                    )
                  }
                />
              </LabeledInput>
              <LabeledInput
                label={t('Names of sibling(s):')}
                htmlFor="environment-ped-sibling-names"
              >
                <TextInput
                  id="environment-ped-sibling-names"
                  value={siblingInfo || ''}
                  updateFieldValue={(newValue) =>
                    this.updatePatientEnvironment('siblingInfo', newValue)
                  }
                  saveFieldValue={(newValue) =>
                    this.updatePatientEnvironment('siblingInfo', newValue)
                  }
                />
              </LabeledInput>
            </>
          ) : null}
        </GroupInputs>
      </div>
    );
  }
}

export default withTranslation()(Environment);
